import { LifeBuoy, type LucideIcon } from 'lucide-react';
import type { ReactElement } from 'react';
import { cn } from '@/shared/utils/formatting';

export const TALK_WITH_US_VARIANTS = ['footer', 'menu-item', 'inline', 'inline-danger'] as const;
export type TalkWithUsVariant = (typeof TALK_WITH_US_VARIANTS)[number];

const VARIANT_CLASSES: Record<TalkWithUsVariant, string> = {
  footer:
    'mt-3 flex w-full items-center gap-2 rounded-lg border border-border-subtle bg-white/3 px-3.5 py-2.5 text-[13px] text-text-secondary transition-all duration-200 hover:border-border-hover hover:bg-white/6 hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
  'menu-item':
    'mx-2 flex items-center gap-3 rounded px-2 py-2 text-sm hover:bg-white/10 transition-colors',
  inline:
    'inline-flex h-9 items-center justify-center gap-1.5 rounded-sm border border-white/10 bg-white/5 px-2.5 text-xs font-semibold text-white/70 transition-colors hover:border-white/20 hover:text-white',
  'inline-danger':
    'inline-flex h-10 items-center justify-center gap-1.5 rounded-sm border border-white/10 bg-white/5 px-4 text-sm font-semibold text-white/80 transition-colors hover:border-white/20 hover:text-white',
};

const ICON_CLASSES: Record<TalkWithUsVariant, string> = {
  footer: 'shrink-0 opacity-60',
  'menu-item': 'w-4 h-4',
  inline: 'h-3.5 w-3.5',
  'inline-danger': 'h-4 w-4',
};

export interface TalkWithUsProps {
  /**
   * Visual variant. `footer` matches the legacy footer mailto card; `menu-item`
   * matches a row inside the user dropdown; `inline` and `inline-danger` are
   * compact buttons for help drawers and error fallbacks.
   */
  variant?: TalkWithUsVariant;
  /** Button label. Defaults to "Talk with us". */
  label?: string;
  /** Lucide icon to render before the label. Defaults to `LifeBuoy`. */
  icon?: LucideIcon;
  /** Click handler — wire to `openCrispChat()` from the platform-side container. */
  onClick: () => void;
  /** Disable the button while Crisp script is loading. */
  disabled?: boolean;
  /** Optional className passthrough for layout overrides at the callsite. */
  className?: string;
}

/**
 * Presentation-only "Talk with us" CTA. Pure props/callbacks — no Crisp,
 * PostHog, or store access. The platform-side `<TalkWithUsButton>` container
 * wires the actual chat trigger.
 */
export function TalkWithUs({
  variant = 'footer',
  label = 'Talk with us',
  icon: Icon = LifeBuoy,
  onClick,
  disabled,
  className,
}: TalkWithUsProps): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        VARIANT_CLASSES[variant],
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className
      )}
    >
      <Icon className={ICON_CLASSES[variant]} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}
